import { Checkbox, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React, {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';

const {
  actions: paymentTypeActions,
  selectors: paymentTypeSelectors
} = slices.paymentTypes;

interface Props {
  onHandleChange: (paymentTypes: string | string[]) => void;
  isMultiple?: boolean;
  isDisabled?: boolean;
  initialValue?: string | string[];
  size?: 'medium' | 'small';
  limitTags?: number;
}

const Component: FC<Props> = ({
  onHandleChange,
  isMultiple = false,
  isDisabled = false,
  initialValue = null,
  size = 'medium',
  limitTags = 3
}) => {
  const dispatch = useAppDispatch();
  const paymentTypes = useAppSelector(
    paymentTypeSelectors.selectGetPaymentTypes
  );

  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<
    string | string[] | null
  >(isMultiple ? [] : null);
  const [focused, setFocused] = useState(false);

  const getPaymentTypes = useCallback(async () => {
    try {
      await dispatch(paymentTypeActions?.getPaymentTypesThunk());
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  const onHandleSelectedChange = (
    event: ChangeEvent<{}>,
    value: string[] | string | null
  ) => {
    setSelectedPaymentTypes(value);

    if (Array.isArray(value)) {
      onHandleChange(value); // multiple selected values
    } else {
      onHandleChange(value ?? ''); // single selected value
    }
  };

  const initializeSelectedPaymentTypes = useCallback(() => {
    if (!paymentTypes || paymentTypes.length === 0 || initialValue == null)
      return;

    if (isMultiple && Array.isArray(initialValue)) {
      const matched = paymentTypes.filter((type) =>
        initialValue.includes(type)
      );
      setSelectedPaymentTypes(matched);
    } else if (!isMultiple && typeof initialValue === 'string') {
      const matched = paymentTypes.find((type) => type === initialValue);
      if (matched) setSelectedPaymentTypes(matched);
    }
  }, [paymentTypes, initialValue, isMultiple]);

  useEffect(() => {
    initializeSelectedPaymentTypes();
  }, [initialValue, paymentTypes, isMultiple, initializeSelectedPaymentTypes]);

  useEffect(() => {
    getPaymentTypes();
  }, [getPaymentTypes]);

  return (
    <Autocomplete
      style={{ background: 'white' }}
      size={size}
      id="payment-type-dropdown"
      options={paymentTypes || []}
      limitTags={limitTags}
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      value={selectedPaymentTypes}
      getOptionLabel={(option) => option || ''}
      onChange={onHandleSelectedChange}
      renderOption={(option, { selected }) =>
        isMultiple ? (
          <>
            <Checkbox checked={selected} style={{ marginRight: 8 }} />
            {option}
          </>
        ) : (
          <>{option}</>
        )
      }
      renderTags={(value: string[]) => {
        return (
          <div
            style={{
              display: 'block',
              whiteSpace: focused ? 'normal' : 'nowrap',
              overflow: focused ? 'visible' : 'hidden',
              textOverflow: focused ? 'unset' : 'ellipsis',
              lineHeight: '1.5em'
            }}
          >
            {value.join(', ')}
            {focused && <span style={{ display: 'block', height: 0 }} />}
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Payment Types"
          variant="outlined"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          InputProps={{
            ...params.InputProps,
            style: { flexWrap: focused ? 'wrap' : 'nowrap' },
            inputProps: {
              ...params.inputProps,
              style: {
                caretColor: focused ? 'black' : 'transparent'
              }
            }
          }}
        />
      )}
      disabled={isDisabled}
    />
  );
};

export const PaymentTypeDropDown = memo(Component);
